import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { api } from "../api/client";
import type { MenuItem } from "../api/types";
import { useBusiness } from "../data/BusinessContext";
import Footer from "../components/Footer";
import IntroSplash, { shouldShowIntro } from "../components/IntroSplash";
import Logo from "../components/Logo";
import Marquee from "../components/Marquee";
import OpenBadge from "../components/OpenBadge";
import Reveal, { RevealWords } from "../components/Reveal";
import SiteNav from "../components/SiteNav";
import { useLanguage } from "../i18n/LanguageContext";
import { DAY_KEYS } from "../lib/hours";
import { imageUrl, siteImage, srcSet, type SiteImageName } from "../lib/images";
import { formatPrice, toCents } from "../lib/money";
import "./HomePage.css";

/**
 * The front door.
 *
 * One long scroll: a photograph you arrive into, the three parts of the
 * day, the room itself, a few dishes, the one sentence the whole brand is
 * built on, then hours and the way there.
 */
export default function HomePage() {
  const { t, language, locale } = useLanguage();
  const business = useBusiness();
  const location = useLocation();
  const reduce = useReducedMotion();
  const [featured, setFeatured] = useState<MenuItem[]>([]);
  const [intro, setIntro] = useState(shouldShowIntro);

  useEffect(() => {
    api
      .menu(language)
      .then((categories) => setFeatured(categories.flatMap((c) => c.items).filter((i) => i.is_favourite).slice(0, 4)))
      .catch(() => setFeatured([]));
  }, [language]);

  // /#house from the nav: scroll once the page has rendered.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const timer = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
    return () => clearTimeout(timer);
  }, [location.hash]);

  // The hero photograph drifts slower than the page — a few percent is plenty.
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const copyY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-30%"]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  // Times come from the dashboard ("Tageszeiten"); the strings are only the fallback.
  const timeOf = (key: "morning" | "afternoon" | "evening", fallback: string) => {
    const part = business.dayparts?.[key];
    return part ? t("daypartTime", { from: part.from, to: part.to }) : fallback;
  };
  const dayparts = [
    { key: "morning", title: t("morningTitle"), time: timeOf("morning", t("morningTime")), body: t("morningBody"), image: "toast" as SiteImageName },
    { key: "afternoon", title: t("afternoonTitle"), time: timeOf("afternoon", t("afternoonTime")), body: t("afternoonBody"), image: "ceramics" as SiteImageName },
    { key: "evening", title: t("eveningTitle"), time: timeOf("evening", t("eveningTime")), body: t("eveningBody"), image: "drink" as SiteImageName },
  ];

  return (
    <div className="home">
      {intro && <IntroSplash onDone={() => setIntro(false)} />}
      <SiteNav overHero />

      {/* --- hero ---------------------------------------------------------- */}
      <section className="hero" ref={heroRef}>
        <motion.div className="hero-photo" style={{ y: photoY }} aria-hidden="true">
          <motion.img
            src={siteImage("interior", 1800)}
            srcSet={srcSet("/img/interior.jpg", [800, 1200, 1800, 2400])}
            sizes="100vw"
            alt=""
            fetchPriority="high"
            decoding="async"
            initial={reduce ? false : { scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          />
          <div className="hero-shade" />
        </motion.div>

        <motion.div className="site-inner hero-inner" style={{ y: copyY, opacity: copyOpacity }} id="main">
          <motion.div
            className="hero-topline"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <OpenBadge hours={business.hours} tone="light" />
          </motion.div>

          <h1 className="hero-title">
            {[t("heroTitle1"), t("heroTitle2"), t("heroTitle3")].map((line, index) => (
              <span key={line} className="hero-line">
                <motion.span
                  initial={reduce ? false : { y: "105%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35 + index * 0.12, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className={index === 2 ? "hero-line-accent" : undefined}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="hero-lead"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {t("heroLead")}
          </motion.p>

          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link className="btn btn-primary btn-lg" to="/reserve">
              {t("heroCtaReserve")} <span className="arrow">→</span>
            </Link>
            <Link className="btn btn-outline btn-lg on-dark" to="/menu">
              {t("heroCtaMenu")}
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-mark"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1.2 }}
          aria-hidden="true"
        >
          <Logo tone="light" variant="lockup" height={110} />
        </motion.div>

        <a
          className="hero-scroll"
          href="#day"
          onClick={(event) => {
            event.preventDefault();
            document.getElementById("day")?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          <span>{t("heroScroll")}</span>
          <span className="hero-scroll-line" aria-hidden="true" />
        </a>
      </section>

      <Marquee text={t("marquee")} />

      {/* --- a day at dähääm ------------------------------------------------ */}
      <section className="section" id="day">
        <div className="site-inner">
          <Reveal className="section-head">
            <p className="eyebrow">{t("dayEyebrow")}</p>
            <h2>
              <RevealWords text={t("dayTitle")} />
            </h2>
          </Reveal>

          <ol className="dayparts">
            {dayparts.map((part, index) => (
              <Reveal key={part.key} delay={index * 0.1}>
                <li className="daypart">
                  <div className="daypart-photo photo photo-zoom">
                    <img
                      src={siteImage(part.image, 800)}
                      srcSet={srcSet(`/img/${part.image}.jpg`, [400, 800, 1200])}
                      sizes="(max-width: 800px) 40vw, 30vw"
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="daypart-time nums">{part.time}</span>
                  </div>
                  <div className="daypart-body">
                    <span className="daypart-index nums">0{index + 1}</span>
                    <h3>{part.title}</h3>
                    <p>{part.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* --- the house ------------------------------------------------------ */}
      <section className="section section-oat" id="house">
        <div className="site-inner house">
          <div className="house-copy">
            <Reveal>
              <p className="eyebrow">{t("roomEyebrow")}</p>
              <h2 className="house-title">
                <RevealWords text={t("roomTitle")} />
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="lead">{t("roomBody1")}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="lead">{t("roomBody2")}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="house-points">
                {[t("roomPoint1"), t("roomPoint2"), t("roomPoint3"), t("roomPoint4")].map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="house-grid">
            <Reveal className="house-cell house-cell-a photo photo-zoom" y={40}>
              <img src={siteImage("sofa", 900)} srcSet={srcSet("/img/sofa.jpg", [500, 900, 1400])} sizes="(max-width: 900px) 100vw, 50vw" alt="" loading="lazy" decoding="async" />
            </Reveal>
            <Reveal className="house-cell house-cell-b photo photo-zoom" y={60} delay={0.1}>
              <img src={siteImage("corner", 900)} srcSet={srcSet("/img/corner.jpg", [500, 900, 1400])} sizes="(max-width: 900px) 100vw, 50vw" alt="" loading="lazy" decoding="async" />
            </Reveal>
            <Reveal className="house-cell house-cell-c photo photo-zoom" y={50} delay={0.2}>
              <img src={siteImage("mirror", 900)} srcSet={srcSet("/img/mirror.jpg", [500, 900, 1400])} sizes="(max-width: 900px) 100vw, 50vw" alt="" loading="lazy" decoding="async" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* --- from the menu -------------------------------------------------- */}
      {featured.length > 0 && (
        <section className="section">
          <div className="site-inner">
            <Reveal className="section-head signature-head">
              <div>
                <p className="eyebrow">{t("signatureEyebrow")}</p>
                <h2>
                  <RevealWords text={t("signatureTitle")} />
                </h2>
              </div>
              <Link className="link" to="/menu">
                {t("viewFullMenu")} →
              </Link>
            </Reveal>

            <ul className="signature">
              {featured.map((item, index) => (
                <Reveal key={item.id} delay={index * 0.08}>
                  <li className="signature-item">
                    <Link to="/menu" className="signature-link">
                      <div className="signature-photo photo photo-zoom">
                        {item.image ? <img src={imageUrl(item.image, 600)} srcSet={srcSet(item.image, [400, 600, 900])} sizes="(max-width: 960px) 50vw, 25vw" alt="" loading="lazy" decoding="async" /> : <span className="signature-mono display">d</span>}
                      </div>
                      <div className="signature-body">
                        <h3 className="signature-name">{item.name}</h3>
                        {item.description && <p className="signature-desc">{item.description}</p>}
                        <span className="signature-price nums">{formatPrice(toCents(item.price), locale)}</span>
                      </div>
                    </Link>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* --- manifesto ------------------------------------------------------ */}
      <section className="manifesto">
        <div className="manifesto-photo photo" aria-hidden="true">
          <img src={siteImage("guests", 1200)} srcSet={srcSet("/img/guests.jpg", [700, 1200, 1800])} sizes="(max-width: 900px) 100vw, 50vw" alt="" loading="lazy" decoding="async" />
        </div>
        <div className="manifesto-panel section-bordeaux">
          <Reveal className="manifesto-inner">
            <blockquote className="manifesto-quote display">
              <RevealWords text={t("manifestoQuote")} />
            </blockquote>
            <p className="manifesto-body">{t("manifestoBody")}</p>
            <Link className="btn btn-outline on-dark" to="/reserve">
              {t("heroCtaReserve")}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* --- hours & directions -------------------------------------------- */}
      <section className="section" id="hours">
        <div className="site-inner find">
          <div className="find-copy">
            <Reveal>
              <p className="eyebrow">{t("findEyebrow")}</p>
              <h2>
                <RevealWords text={t("findTitle")} />
              </h2>
              <p className="lead">{t("findLead")}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <dl className="facts">
                <div className="fact">
                  <dt>{t("addressLabel")}</dt>
                  <dd>
                    {business.address}
                    <br />
                    {business.city}
                  </dd>
                </div>
                <div className="fact">
                  <dt>{t("phoneLabel")}</dt>
                  <dd>
                    <a className="link" href={`tel:${business.phone.replace(/\s/g, "")}`}>
                      {business.phone}
                    </a>
                  </dd>
                </div>
                {business.email && (
                  <div className="fact">
                    <dt>{t("emailLabel")}</dt>
                    <dd>
                      <a className="link" href={`mailto:${business.email}`}>
                        {business.email}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
              <a className="btn btn-outline" href={business.mapsUrl} target="_blank" rel="noreferrer">
                {t("openMaps")} <span className="arrow">↗</span>
              </a>
            </Reveal>
          </div>

          <Reveal className="hours-card" delay={0.15}>
            <div className="hours-card-head">
              <span className="eyebrow">{t("hoursLabel")}</span>
              <OpenBadge hours={business.hours} />
            </div>
            <ul className="hours-list">
              {DAY_KEYS.map((day) => {
                const h = business.hours[day];
                const isToday = day === DAY_KEYS[(new Date().getDay() + 6) % 7];
                return (
                  <li key={day} className={isToday ? "is-today" : undefined}>
                    <span>{t(day)}</span>
                    <span className="hours-dots" aria-hidden="true" />
                    <span className="nums">{h ? `${h.open} – ${h.close}` : t("closedLabel")}</span>
                  </li>
                );
              })}
            </ul>
          </Reveal>
        </div>
      </section>

      <Footer business={business} />
    </div>
  );
}
